import { numberWithComma } from "../../../utility";
import { FORMAT_TYPES } from "./salaryBreakupConfig";

/**
 * Normalizes raw salary_based_payout_data from the backend API into a clean,
 * consistent array of column definitions.
 *
 * Handles both Array and Object (associative array) representations from the API.
 *
 * @param {Array|Object} rawData - The raw salary_based_payout_data
 * @returns {Array<{ id: string, title: string, subTitle?: string, data: Object }>}
 */
export const normalizeSalaryBreakupData = (rawData) => {
  if (!rawData) return [];

  let list = [];

  if (Array.isArray(rawData)) {
    list = rawData;
  } else if (typeof rawData === "object") {
    // If keyed by period name or index
    list = Object.entries(rawData).map(([key, value]) => {
      if (value && typeof value === "object") {
        return {
          ...value,
          __objectKey: key,
        };
      }
      return value;
    });
  }

  // Filter out non-object or null entries
  const validRecords = list.filter((item) => item && typeof item === "object");

  return validRecords.map((item, index) => {
    const periodNumber = index + 1;
    const customTitle =
      item?.title ||
      item?.label ||
      item?.period_name ||
      item?.structure_name ||
      (item?.__objectKey && isNaN(item.__objectKey)
        ? item.__objectKey.replace(/_/g, " ")
        : null);

    const fromDate = item?.salary_from_date || item?.from_date;
    const toDate = item?.salary_to_date || item?.to_date;

    let dateRange = null;
    if (fromDate && toDate) {
      dateRange = `${fromDate} to ${toDate}`;
    } else if (fromDate) {
      dateRange = `From ${fromDate}`;
    } else if (toDate) {
      dateRange = `Up to ${toDate}`;
    } else if (item?.date_range) {
      dateRange = item.date_range;
    }

    const title = customTitle || `Breakup ${periodNumber}`;
    const subTitle = dateRange || (customTitle ? `Period ${periodNumber}` : null);

    return {
      id: `col-${index}`,
      index,
      title,
      subTitle,
      dateRange,
      data: item,
    };
  });
};

/**
 * Formats values according to the column/field format strategy.
 *
 * @param {any} value - Cell raw value
 * @param {string} formatType - One of FORMAT_TYPES
 * @returns {string} - Formatted display string
 */
export const formatBreakupCellValue = (value, formatType) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const num = typeof value === "number" ? value : Number(value);
  const isValidNumber = !isNaN(num);

  switch (formatType) {
    case FORMAT_TYPES.CURRENCY: {
      if (!isValidNumber) return String(value);
      const rounded = Math.round(num);
      return `₹ ${numberWithComma(rounded) || "0"}`;
    }

    case FORMAT_TYPES.HOURS: {
      if (!isValidNumber) {
        // May already be formatted as "02:30" or string
        return String(value);
      }
      return Number.isInteger(num) ? `${num}` : `${parseFloat(num.toFixed(2))}`;
    }

    case FORMAT_TYPES.NUMBER: {
      if (!isValidNumber) return String(value);
      return numberWithComma(Math.round(num)) || "0";
    }

    case FORMAT_TYPES.TEXT:
    default:
      return String(value);
  }
};
